import React from 'react';
import type {
    OperationDefinitionDto,
    OperationRunDto,
    OperationRunDetailsDto,
    OperationScheduleDto,
    OperationScheduleSaveRequest
} from '../../types';
import { formatDateTime, formatDuration, statusLabel, triggerLabel, riskLabel } from './utils.tsx';
import { getOperationExtension } from './operationExtensions';

interface Props {
    selectedDefinition: OperationDefinitionDto | null;
    selectedSchedule: OperationScheduleDto | null;
    selectedOpRuns: OperationRunDto[];
    selectedRunId: number | null;
    editParams: string;
    runLoading: string | null;
    scheduleLoading: boolean;
    editCron: string;
    editTimezone: string;
    onEditParamsChange: (value: string) => void;
    onEditCronChange: (value: string) => void;
    onEditTimezoneChange: (value: string) => void;
    onRun: () => Promise<void>;
    onSaveSchedule: () => Promise<void>;
    onDeleteSchedule: () => Promise<void>;
    onOpenRunLog: (runId: number) => void;
}

const OperationDetailPanel: React.FC<Props> = ({
    selectedDefinition,
    selectedSchedule,
    selectedOpRuns,
    selectedRunId,
    editParams,
    runLoading,
    scheduleLoading,
    editCron,
    editTimezone,
    onEditParamsChange,
    onEditCronChange,
    onEditTimezoneChange,
    onRun,
    onSaveSchedule,
    onDeleteSchedule,
    onOpenRunLog
}) => {
    if (!selectedDefinition) return null;

    const extension = getOperationExtension(selectedDefinition.code);

    return (
        <>
            <div className="operations-detail-header">
                <h2>{selectedDefinition.title}</h2>
                <span className={`operations-risk risk-${selectedDefinition.riskLevel.toLowerCase()}`}>
                    {riskLabel[selectedDefinition.riskLevel] ?? selectedDefinition.riskLevel}
                </span>
            </div>
            <p className="operations-detail-desc">{selectedDefinition.description}</p>

            {selectedDefinition.parameters.length > 0 && (
                <section className="operations-section">
                    <h3>Параметры</h3>
                    {extension?.renderParameterEditor ? (
                        extension.renderParameterEditor({
                            editParams,
                            onEditParamsChange
                        })
                    ) : (
                        <textarea
                            className="operations-json"
                            value={editParams}
                            onChange={e => onEditParamsChange(e.target.value)}
                            rows={Math.max(3, editParams.split('\n').length)}
                            spellCheck={false}
                        />
                    )}
                </section>
            )}

            {selectedDefinition.supportsManualRun && (
                <section className="operations-section">
                    <h3>Ручной запуск</h3>
                    <button
                        className="btn btn-primary"
                        disabled={runLoading === selectedDefinition.code}
                        onClick={onRun}
                    >
                        {runLoading === selectedDefinition.code ? (
                            <>
                                <span className="btn-spinner spinner-border spinner-border-sm" role="status" />
                                Запуск...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-play-fill"></i>
                                Запустить
                            </>
                        )}
                    </button>
                </section>
            )}

            {selectedDefinition.supportsSchedule && (
                <section className="operations-section">
                    <h3>Расписание</h3>
                    {selectedSchedule?.enabled ? (
                        <div className="operations-schedule-info">
                            <span className="operations-status status-succeeded">Включено</span>
                            <div className="operations-schedule-details">
                                <div><strong>Cron:</strong> {selectedSchedule.cron}</div>
                                <div><strong>Timezone:</strong> {selectedSchedule.timeZoneId}</div>
                                <div><strong>Следующий запуск:</strong> {formatDateTime(selectedSchedule.nextExecution)}</div>
                                <div><strong>Последний запуск:</strong> {formatDateTime(selectedSchedule.lastExecution)}</div>
                                {selectedSchedule.error && (
                                    <div className="operations-schedule-error">{selectedSchedule.error}</div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="operations-empty">Расписание не настроено.</div>
                    )}

                    <div className="operations-schedule-form">
                        <div className="operations-schedule-fields">
                            <div className="form-group-custom operations-cron-field">
                                <label className="form-label-custom">
                                    <i className="bi bi-clock"></i>
                                    Cron выражение
                                </label>
                                <input
                                    className="form-input-custom"
                                    type="text"
                                    value={editCron}
                                    onChange={e => onEditCronChange(e.target.value)}
                                    placeholder="0 2 * * *"
                                />
                            </div>
                            <div className="form-group-custom operations-timezone-field">
                                <label className="form-label-custom">
                                    <i className="bi bi-globe"></i>
                                    Timezone
                                </label>
                                <select
                                    className="form-select-custom"
                                    value={editTimezone}
                                    onChange={e => onEditTimezoneChange(e.target.value)}
                                >
                                    <option value="UTC">UTC</option>
                                    <option value="Europe/Moscow">Europe/Moscow</option>
                                    <option value="Europe/Simferopol">Europe/Simferopol</option>
                                    <option value="Asia/Yekaterinburg">Asia/Yekaterinburg</option>
                                </select>
                            </div>
                        </div>
                        <div className="operations-schedule-actions">
                            <button
                                className="btn btn-primary-custom"
                                disabled={scheduleLoading}
                                onClick={onSaveSchedule}
                            >
                                {scheduleLoading ? (
                                    <>
                                        <span className="btn-spinner spinner-border spinner-border-sm" role="status" />
                                        Сохранение...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg"></i>
                                        {selectedSchedule?.enabled ? 'Обновить' : 'Сохранить'}
                                    </>
                                )}
                            </button>
                            {selectedSchedule?.enabled && (
                                <button
                                    className="btn btn-secondary-custom"
                                    disabled={scheduleLoading}
                                    onClick={onDeleteSchedule}
                                >
                                    <i className="bi bi-x-lg"></i>
                                    Удалить
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            )}

            <section className="operations-section">
                <h3>Последние запуски операции</h3>
                {selectedOpRuns.length > 0 ? (
                    <div className="operations-table-wrap">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Статус</th>
                                    <th>Триггер</th>
                                    <th>Запущено</th>
                                    <th>Длительность</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedOpRuns.map(run => (
                                    <tr
                                        key={run.id}
                                        className={selectedRunId === run.id ? 'table-active' : ''}
                                        onClick={() => onOpenRunLog(run.id)}
                                    >
                                        <td>{run.id}</td>
                                        <td>
                                            <span className={`operations-status status-${run.status.toLowerCase()}`}>
                                                {statusLabel[run.status] ?? run.status}
                                            </span>
                                        </td>
                                        <td>{triggerLabel[run.trigger] ?? run.trigger}</td>
                                        <td>{formatDateTime(run.createdAt)}</td>
                                        <td>{formatDuration(run)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="operations-empty">Запусков этой операции пока нет.</div>
                )}
            </section>
        </>
    );
};

export default OperationDetailPanel;
